import { MapPin, CheckCircle2, Share2 } from "lucide-react";
import { t } from "../../i18n";
import { cityCenter } from "../../lib/cities";
import { AddressAutocomplete } from "../AddressAutocomplete";
import { DeliveryMap } from "../DeliveryMap";
import { Choice } from "./Controls";

export const AddressQuestion = ({ locale, idx, r, onKnown, onUnknown, answerRequired }) => (
  <div className="space-y-3 pt-1">
    <label className="smallcaps">{t(locale, "addressQuestion")}</label>
    <div className="flex flex-wrap gap-2">
      <Choice on={r.knows_address === true} testid={`address-known-${idx}`} label={t(locale, "addressKnown")} onClick={onKnown} />
      <Choice on={r.knows_address === false} testid={`address-unknown-${idx}`} label={t(locale, "addressUnknown")} onClick={onUnknown} />
    </div>
    {r.knows_address === undefined && <p role="status" className="text-danger text-xs" data-testid={`address-question-error-${idx}`}>{answerRequired}</p>}
  </div>
);

const TextInput = ({ testid, label, value, onChange, placeholder, autoComplete }) => (
  <div className="space-y-1">
    <label className="smallcaps">{label}</label>
    <input data-testid={testid} className="field" placeholder={placeholder} autoComplete={autoComplete}
           value={value} onChange={(e) => onChange(e.target.value)} />
  </div>
);

export const KnownAddress = ({ locale, idx, g, r, errs, set, apiKey }) => (
  <div className="space-y-3" data-testid={`address-block-${idx}`}>
    <div className="space-y-1">
      <label className="smallcaps">{t(locale, "addressLine1")}</label>
      <AddressAutocomplete
        apiKey={apiKey}
        value={r.address_line1}
        onChange={(v) => set("address_line1", v)}
        onSelect={(p) => {
          set("address_line1", p.line1);
          if (p.lat && p.lng && !r.delivery_location) {
            set("delivery_location", { lat: p.lat, lng: p.lng, source: "autocomplete", address: p.formatted });
          }
        }}
        city={cityCenter(g.city)}
        placeholder={t(locale, "addressLine1Placeholder")}
        testid={`recipient-address-line1-${idx}`}
      />
      <p className="text-xs text-muted">{t(locale, "addressLine1Help")}</p>
    </div>
    <div className="grid sm:grid-cols-2 gap-3">
      <TextInput testid={`recipient-apartment-${idx}`} label={t(locale, "buildingApartment")} autoComplete="address-line2"
                 placeholder={t(locale, "buildingApartmentPlaceholder")} value={r.apartment} onChange={(v) => set("apartment", v)} />
      <TextInput testid={`recipient-address-line2-${idx}`} label={t(locale, "addressLine2")} autoComplete="address-line3"
                 placeholder={t(locale, "addressLine2Placeholder")} value={r.address_line2} onChange={(v) => set("address_line2", v)} />
    </div>
    <TextInput testid={`recipient-landmark-${idx}`} label={t(locale, "landmark")} value={r.landmark} onChange={(v) => set("landmark", v)} />
    {errs.address && (r.address_line1 || r.apartment || r.address_line2) ?
      <p className="text-danger text-xs" data-testid={`recipient-address-error-${idx}`}>{errs.address}</p> : null}
  </div>
);

const LocationRequest = ({ locale, idx, req, onShare, onCopy }) => (
  <div className="card p-3 space-y-2 bg-plaster/60" data-testid={`loc-request-${idx}`}>
    <p className="smallcaps text-xs">{t(locale, "shareLink")}</p>
    <input readOnly className="field text-xs" value={req.url} data-testid={`loc-request-url-${idx}`} onFocus={(e) => e.target.select()} />
    <div className="flex flex-wrap gap-2">
      <button type="button" className="btn-primary text-xs inline-flex items-center gap-1.5" data-testid={`loc-share-${idx}`} onClick={onShare}>
        <Share2 size={13} /> {t(locale, "shareTheLink")}
      </button>
      <button type="button" className="btn-secondary text-xs whitespace-nowrap" data-testid={`loc-copy-${idx}`} onClick={onCopy}>
        {req.copied ? t(locale, "copied") : t(locale, "copyLink")}
      </button>
    </div>
  </div>
);

export const UnknownAddress = ({ locale, idx, r, errs, set, req, onAsk, onShare, onCopy }) => (
  <div className="space-y-3" data-testid={`no-address-block-${idx}`}>
    <p className="text-xs text-muted">{t(locale, "addressUnknownHelp")}</p>
    <div className="space-y-1">
      <label className="smallcaps">{t(locale, "landmark")}</label>
      <input data-testid={`recipient-landmark-${idx}`} className="field" value={r.landmark}
             placeholder={t(locale, "landmark")} onChange={(e) => set("landmark", e.target.value)} />
      {errs.address ? <p className="text-danger text-xs" data-testid={`neighbourhood-error-${idx}`}>{errs.address}</p> : null}
    </div>
    {req ? <LocationRequest locale={locale} idx={idx} req={req} onShare={onShare} onCopy={onCopy} /> : (
      <button type="button" data-testid={`ask-recipient-${idx}`} className="btn-ghost text-xs" onClick={onAsk}>{t(locale, "askRecipient")}</button>
    )}
  </div>
);

export const DeliveryPin = ({ locale, idx, g, r, set, maps }) => (
  <div className="space-y-3 pt-3 border-t border-brass/20" data-testid={`delivery-location-${idx}`}>
    {!r.pin_open && !r.delivery_location ? (
      <button type="button" data-testid={`open-pin-${idx}`} className="btn-ghost text-xs inline-flex items-center gap-1.5"
              onClick={() => set("pin_open", true)}>
        <MapPin size={13} className="text-brass" /> {t(locale, "exactLocation")} · {t(locale, "optionalLabel")}
      </button>
    ) : (
      <>
        <div className="flex items-center gap-2">
          <MapPin size={15} className="text-brass" />
          <label className="smallcaps">{t(locale, "exactLocation")}</label>
          <span className="text-xs text-muted">· {t(locale, "optionalLabel")}</span>
        </div>
        <p className="text-xs text-muted">{t(locale, "pinHelp")}</p>
        {maps.google_maps_api_key ? (
          <DeliveryMap
            apiKey={maps.google_maps_api_key}
            locale={locale}
            center={cityCenter(g.city)}
            autocomplete={maps.address_autocomplete_enabled !== false}
            testid={`delivery-map-${idx}`}
            value={r.delivery_location ? { lat: r.delivery_location.lat, lng: r.delivery_location.lng } : null}
            onChange={(loc) => {
              set("delivery_location", { lat: loc.lat, lng: loc.lng, source: "pin", address: loc.address || r.delivery_location?.address || "" });
              if (loc.address && !r.address_line1) set("address_line1", loc.address);
            }}
          />
        ) : null}
        {r.delivery_location ? (
          <p className="text-xs text-ink flex items-center gap-1" data-testid={`pin-set-${idx}`}>
            <CheckCircle2 size={13} className="text-brass" /> {t(locale, "pinSet")}
          </p>
        ) : null}
      </>
    )}
  </div>
);
